import { useAppStore } from '@/app/providers/app-store'

export type Locale = 'ar' | 'en'

/**
 * Bilingual string. Both locales are required to prevent half-translated UI.
 */
export type Bilingual = { ar: string; en: string }

/**
 * Co-locate strings as `T = { key: { ar, en } }` and read with `t('key')`.
 * Forces both locales at compile time — no half-translated UI.
 */
export function useTranslation<T extends Record<string, Bilingual>>(table: T) {
  const locale = useAppStore((s) => s.locale)
  const dir: 'rtl' | 'ltr' = locale === 'ar' ? 'rtl' : 'ltr'
  const isRtl = locale === 'ar'

  const t = (key: keyof T): string => table[key][locale]

  return { t, locale, dir, isRtl }
}

/**
 * For places that don't co-locate their own table — pick a bilingual literal.
 */
export function useLocale() {
  const locale = useAppStore((s) => s.locale)
  return {
    locale,
    dir: (locale === 'ar' ? 'rtl' : 'ltr') as 'rtl' | 'ltr',
    isRtl: locale === 'ar',
    pick: (b: Bilingual) => b[locale],
  }
}

/**
 * Helper to build a typed translation table. Keeps the call site terse
 * and gives auto-complete on keys.
 */
export function defineT<T extends Record<string, Bilingual>>(t: T): T {
  return t
}
